import React, { useState } from 'react';
import { ArrowLeft, X, ChevronRight, Edit3, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { MediaUpload } from '../components/create/MediaUpload';
import { ImageEditor } from '../components/create/ImageEditor';
import { CaptionEditor } from '../components/create/CaptionEditor';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { MediaFile, ImageAdjustments, User } from '../types';
import { users } from '../data/mockData';

type CreateStep = 'upload' | 'edit' | 'caption' | 'sharing';

export const CreatePostPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState<CreateStep>('upload');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [location, setLocation] = useState('');
  const [taggedUsers, setTaggedUsers] = useState<User[]>([]);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState('');

  const handleMediaAdd = (newFiles: MediaFile[]) => {
    setMediaFiles(prev => [...prev, ...newFiles]);
    if (mediaFiles.length === 0 && newFiles.length > 0) {
      setCurrentStep('edit');
    }
  };

  const handleMediaRemove = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    if (mediaFiles.length === 1) {
      setCurrentStep('upload');
    }
  };

  const handleMediaReorder = (fromIndex: number, toIndex: number) => {
    setMediaFiles(prev => {
      const newFiles = [...prev];
      const [movedFile] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, movedFile);
      return newFiles;
    });
  };

  const handleImageEdit = (editedImageUrl: string, adjustments: ImageAdjustments) => {
    if (editingIndex !== null) {
      setMediaFiles(prev => prev.map((file, index) => 
        index === editingIndex 
          ? { ...file, url: editedImageUrl }
          : file
      ));
    }
    setEditingIndex(null);
  };

  const uploadMediaToSupabase = async (file: File): Promise<string> => {
    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `posts/${user?.id}/${fileName}`;

      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from('media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.warn('Storage upload failed, using data URL fallback:', error);
        // Fallback to data URL if storage upload fails
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (err) {
      console.warn('Storage upload error, using data URL fallback:', err);
      // Fallback to data URL
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    }
  };

  const handleShare = async () => {
    if (!user || mediaFiles.length === 0) return;

    setIsSharing(true);
    setError('');

    try {
      // Create the post
      const { data: post, error: postError } = await supabase
        .from('posts')
        .insert([
          {
            user_id: user.id,
            caption,
            location: location || null,
          }
        ])
        .select()
        .single();

      if (postError) throw postError;

      // Upload media files and create media records
      for (let i = 0; i < mediaFiles.length; i++) {
        const mediaFile = mediaFiles[i];
        
        // Upload file to storage
        const mediaUrl = await uploadMediaToSupabase(mediaFile.file);
        
        // Create media record
        const { error: mediaError } = await supabase
          .from('post_media')
          .insert([
            {
              post_id: post.id,
              media_url: mediaUrl,
              media_type: mediaFile.type,
              order_index: i,
              width: mediaFile.width,
              height: mediaFile.height,
            }
          ]);

        if (mediaError) throw mediaError;
      }

      // Create hashtag records
      for (const hashtag of hashtags) {
        const { error: hashtagError } = await supabase
          .from('post_hashtags')
          .insert([
            {
              post_id: post.id,
              hashtag: hashtag.toLowerCase(),
            }
          ]);

        if (hashtagError) throw hashtagError;
      }

      // Create user tag records
      for (const taggedUser of taggedUsers) {
        const { error: tagError } = await supabase
          .from('post_user_tags')
          .insert([
            {
              post_id: post.id,
              tagged_user_id: taggedUser.id,
            }
          ]);

        if (tagError) throw tagError;
      }

      // Navigate to home page
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Error creating post:', err);
      setError(err.message || 'Failed to create post');
    } finally {
      setIsSharing(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'upload': return 'Create New Post';
      case 'edit': return 'Edit Media';
      case 'caption': return 'Share Post';
      case 'sharing': return 'Sharing...';
      default: return 'Create New Post';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'upload': return mediaFiles.length > 0;
      case 'edit': return true;
      case 'caption': return caption.trim().length > 0;
      default: return false;
    }
  };

  const handleNext = () => {
    switch (currentStep) {
      case 'upload':
        if (mediaFiles.length > 0) setCurrentStep('edit');
        break;
      case 'edit':
        setCurrentStep('caption');
        break;
      case 'caption':
        handleShare();
        break;
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'edit':
        setCurrentStep('upload');
        break;
      case 'caption':
        setCurrentStep('edit');
        break;
      default:
        navigate('/');
        break;
    }
  };

  return (
    <>
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <button onClick={handleBack} className="text-gray-600">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-lg font-medium">{getStepTitle()}</h1>
              {currentStep !== 'upload' && (
                <Button
                  onClick={handleNext}
                  variant="link"
                  disabled={!canProceed() || isSharing}
                  isLoading={isSharing}
                >
                  {currentStep === 'caption' ? 'Share' : 'Next'}
                  {currentStep !== 'caption' && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              )}
              {currentStep === 'upload' && (
                <Link to="/" className="text-gray-600">
                  <X className="h-5 w-5" />
                </Link>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-b border-red-200 text-red-600 px-4 py-3 text-sm">
                {error}
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              {currentStep === 'upload' && (
                <MediaUpload
                  mediaFiles={mediaFiles}
                  onMediaAdd={handleMediaAdd}
                  onMediaRemove={handleMediaRemove}
                  onMediaReorder={handleMediaReorder}
                  maxFiles={10}
                />
              )}

              {currentStep === 'edit' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {mediaFiles.map((media, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          {media.type === 'image' ? (
                            <img
                              src={media.url}
                              alt={`Media ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <video
                              src={media.url}
                              className="w-full h-full object-cover"
                              muted
                            />
                          )}
                        </div>
                        
                        {media.type === 'image' && (
                          <button
                            onClick={() => setEditingIndex(index)}
                            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70"
                            title="Apply filters and edit"
                          >
                            <Sparkles className="h-4 w-4" />
                          </button>
                        )}

                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Image Enhancement
                    </h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Click the sparkle icon on images to apply filters</li>
                      <li>• Choose from 12 professional filters including Vintage, B&W, and Dramatic</li>
                      <li>• Fine-tune brightness, contrast, and saturation</li>
                      <li>• Rotate images and apply multiple adjustments</li>
                      <li>• Preview changes in real-time before applying</li>
                    </ul>
                  </div>
                </div>
              )}

              {currentStep === 'caption' && (
                <CaptionEditor
                  caption={caption}
                  onCaptionChange={setCaption}
                  hashtags={hashtags}
                  onHashtagsChange={setHashtags}
                  location={location}
                  onLocationChange={setLocation}
                  taggedUsers={taggedUsers}
                  onTaggedUsersChange={setTaggedUsers}
                  availableUsers={users}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Editor Modal */}
      {editingIndex !== null && mediaFiles[editingIndex] && (
        <ImageEditor
          imageUrl={mediaFiles[editingIndex].url}
          onSave={handleImageEdit}
          onCancel={() => setEditingIndex(null)}
        />
      )}
    </>
  );
};